"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import { Label } from "@/components/ui/label";
import {
  ModeToggle,
  type Participant,
  suggestionToParticipant,
  TeamCard,
  ValidationWarning,
} from "./base-match-template";
import { ActivityCourtPreview, ActivityDisplayPreview } from "./previews";
import { TableTennisScoreEditor } from "./score-editors/table-tennis-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type TableTennisMode = "singles" | "doubles";

export type TableTennisTemplateData = {
  mode: TableTennisMode;
  matchScore: { yourGames: number; opponentGames: number };
  games?: Array<{ yourScore: number; opponentScore: number }>;
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type TableTennisParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type TableTennisTemplateProps = {
  currentUser: TableTennisParticipant;
  onChange: (
    data: TableTennisTemplateData,
    participants: TableTennisParticipant[],
  ) => void;
  initialData?: Partial<TableTennisTemplateData>;
};

export function TableTennisTemplate({
  currentUser,
  onChange,
  initialData,
}: TableTennisTemplateProps) {
  const [mode, setMode] = useState<TableTennisMode>(
    initialData?.mode ?? "singles",
  );
  const [participants, setParticipants] = useState<TableTennisParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));
  type TableTennisScorePart = Pick<
    TableTennisTemplateData,
    "matchScore" | "games"
  >;
  const [scorePart, setScorePart] = useState<TableTennisScorePart>(() => ({
    matchScore: initialData?.matchScore ?? { yourGames: 0, opponentGames: 0 },
    games: initialData?.games ?? [],
  }));

  const maxParticipants = mode === "singles" ? 2 : 4;
  const yourTeamMax = mode === "singles" ? 1 : 2;
  const opponentTeamMax = mode === "singles" ? 1 : 2;

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    const data: TableTennisTemplateData = {
      mode,
      matchScore: scorePart.matchScore,
      games: scorePart.games ?? [],
      positions: teams,
      hasScore: true,
    };
    onChange(data, participants);
  }, [mode, scorePart, teams, participants, onChange]);

  const handleModeChange = (newMode: TableTennisMode) => {
    setMode(newMode);
    if (newMode === "singles") {
      const yourTeamFiltered = yourTeam.slice(0, 1);
      const opponentFiltered = opponentTeam.slice(0, 1);
      const newParticipants = [...yourTeamFiltered, ...opponentFiltered];
      if (!newParticipants.find((p) => p.id === currentUser.id)) {
        newParticipants.unshift(currentUser);
      }
      setParticipants(newParticipants);
      const newTeams: Record<string, "your-team" | "opponent-team"> = {
        [currentUser.id]: "your-team",
      };
      for (const p of opponentFiltered) {
        newTeams[p.id] = "opponent-team";
      }
      setTeams(newTeams);
    }
  };

  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    if (participants.length >= maxParticipants) return;
    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    const teamMax = team === "your-team" ? yourTeamMax : opponentTeamMax;
    if (teamCount >= teamMax) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourGames = scorePart.matchScore.yourGames;
  const opponentGames = scorePart.matchScore.opponentGames;
  const yourWon = yourGames > opponentGames;
  const opponentWon = opponentGames > yourGames;
  const isDraw = yourGames === opponentGames && yourGames > 0;
  const isYourTeamComplete = yourTeam.length === yourTeamMax;
  const isOpponentComplete = opponentTeam.length === opponentTeamMax;
  const isComplete = isYourTeamComplete && isOpponentComplete;

  return (
    <div className="space-y-6">
      <ModeToggle
        label="Match Type"
        options={[
          { value: "singles", label: "Singles" },
          { value: "doubles", label: "Doubles" },
        ]}
        value={mode}
        onChange={handleModeChange}
      />

      <div className="grid grid-cols-2 gap-3">
        <TeamCard
          label="Team 1"
          isYourTeam
          players={yourTeam}
          maxPlayers={yourTeamMax}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon || isDraw}
          isComplete={isYourTeamComplete}
        />
        <TeamCard
          label="Team 2"
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={opponentTeamMax}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
          isComplete={isOpponentComplete}
        />
      </div>

      {!isComplete && (
        <ValidationWarning
          message={
            !isYourTeamComplete && !isOpponentComplete
              ? "Add players to both sides to post"
              : !isOpponentComplete
                ? `Add ${opponentTeamMax - opponentTeam.length} more opponent${opponentTeamMax - opponentTeam.length > 1 ? "s" : ""}`
                : ""
          }
        />
      )}

      <TableTennisScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) =>
          setScorePart(data as unknown as TableTennisScorePart)
        }
      />

      {participants.length >= 2 && (
        <div className="space-y-2">
          <Label className="text-text-primary">Preview</Label>
          <ActivityCourtPreview
            activityId="table-tennis"
            mode={mode}
            yourTeam={yourTeam}
            opponentTeam={opponentTeam}
            yourWon={yourWon}
          />
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT (for feed)
// ──────────────────────────────────────────────────────────────────────────────

type TableTennisDisplayProps = {
  data: TableTennisTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function TableTennisDisplay({
  data,
  participants,
}: TableTennisDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="table-tennis"
      data={data}
      participants={participants}
    />
  );
}
